import Image from 'next/image'
import { FC, useMemo } from 'react'
import useSWR from 'swr'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import { ChattedCharacterListRes, StoryDetail } from 'Types/character'
import { SMALL_IMG_WIDTH } from '@/constant/img'
import { characterProfileState } from 'Store/chat'
import { deleteChatConfirmModalVisibleState } from 'Store/modal'
import {
  getCharacterPublicByUser,
  getCharacterPublic,
  getStoryPublic,
} from 'Apis/character'
import {
  getSimulatorGameDetail,
  getSimulatorGameDetailUrl,
} from '@/apis/simulatorGame'
import { transformImage } from 'Utils/img'
import { formatNumber, formatTimestampToTimeAgo } from 'Utils/func'
import MessageIcon from '../svg/MessageIcon'
import { homeNSFWState } from 'Store/home'
import { userState } from 'Store/user'
import useI18n from 'Hooks/useI18n'
import { replaceCharIntroductionPlaceHolders } from '@/lib/llm'
import { DEFAULT_LOCALE } from '@/constant/lang'

type CharacterProfileCardProps = {
  character: ChattedCharacterListRes
}

const RecentTalkCharacterProfileCard: FC<CharacterProfileCardProps> = ({
  character,
}: CharacterProfileCardProps) => {
  const { t, lang } = useI18n()
  const {
    item_id,
    item_name,
    latest_time,
    message_num,
    introduction,
    face_url,
    item_type,
    game_id,
    content_type,
  } = character
  const hideDescriptionPreview = item_type === 'character' && content_type === 2
  const gameId = String(game_id || item_id || '')
  const user = useRecoilValue(userState)
  const nsfw = useRecoilValue(homeNSFWState)
  const setCharacterProfileState = useSetRecoilState(characterProfileState)
  const setModalOpen = useSetRecoilState(deleteChatConfirmModalVisibleState)

  const { data: characterGetResData } = useSWR(
    item_type === 'game'
      ? null
      : item_type === 'story'
        ? {
            endpoint: '/story/detail',
            req: { item_id, nsfw: true, language_code: lang },
            user_id: user?.user_id,
          }
        : {
            endpoint: '/character/get/public',
            user_id: user?.user_id,
            req: { item_id, nsfw: true, language_code: lang },
          },
    async ({ req: { item_id, nsfw, language_code } }) => {
      if (item_type === 'story') {
        return getStoryPublic(item_id, language_code) as Promise<StoryDetail>
      }
      return user?.user_id
        ? getCharacterPublicByUser(item_id, nsfw, language_code)
        : getCharacterPublic(item_id, nsfw, language_code)
    }
  )

  const { data: gameData } = useSWR(
    item_type === 'game' && gameId
      ? getSimulatorGameDetailUrl(gameId, lang)
      : null,
    () => getSimulatorGameDetail(gameId, lang)
  )

  const { interactions, memories, connectors } = useMemo(() => {
    if (item_type === 'game') {
      return {
        interactions: gameData?.stats?.studio_chat_count ?? 0,
        memories: 0,
        connectors: gameData?.stats?.connection_count ?? 0,
      }
    }

    const interactions = characterGetResData?.stats.total_messages ?? 0
    const memories = characterGetResData?.stats.public_plots ?? 0
    const connectors = characterGetResData?.stats.connection_count ?? 0
    return { interactions, memories, connectors }
  }, [characterGetResData, gameData, item_type])

  const handleDelete = () => {
    setCharacterProfileState(character)
    setModalOpen(true)
  }

  return (
    <div className="shadow-[0px 2px 4px 0px rgba(255,255,255,0.05)] rounded-tl-0 relative w-[360px] rounded-[10px] border border-tertiary-border bg-[rgba(0,0,0,0.24)] bg-gradient-to-b from-[#000000CC] via-[#572F66CC] via-50% to-[#1D1515CC] p-4">
      <div
        className="absolute right-4 top-4 h-[27px] cursor-pointer whitespace-nowrap rounded-2xl border border-secondary-border py-[6px] pl-[10px] pr-[8px] text-center text-xs leading-[14px] text-white hover:border-hover-border"
        onClick={handleDelete}
      >
        {t('Delete Chat')}
      </div>
      <div className="mb-[10px] flex gap-2">
        <div
          className="avatar rounded-[10px] border border-secondary-border p-[5px]"
          style={{
            background:
              'linear-gradient(180deg, rgba(0,0,0,0.5) 0%, rgba(87,47,102,0.5) 50%, rgba(29,21,21,0.5) 100%)',
          }}
        >
          <div className="relative h-[46px] w-[46px] overflow-hidden rounded-full border border-white/50">
            <Image
              unoptimized
              className="object-cover"
              src={transformImage(face_url, SMALL_IMG_WIDTH)}
              alt={item_name}
              fill={true}
            />
          </div>
        </div>
        <div className="flex flex-col justify-between pb-[8px] pt-[5px] text-white">
          <div className="max-w-[50px] overflow-hidden text-ellipsis whitespace-nowrap text-base leading-5">
            {item_name}
          </div>
          <div className="items-center text-sm leading-4">
            <span>{formatTimestampToTimeAgo(latest_time * 1000, lang)}</span>
            <span className="mx-2 inline-block h-[10px] w-[1px] bg-white"></span>
            <span className="item-center inline-flex gap-1 align-bottom">
              <MessageIcon />
              <span>{formatNumber(Number(message_num))}</span>
            </span>
          </div>
        </div>
      </div>
      <div className="mb-2 flex min-h-[64px] rounded-lg bg-[rgba(255,255,255,0.2)] px-1 py-3">
        <div className="flex min-w-0 flex-1 flex-col items-center justify-between border-r border-r-secondary-border last:border-r-0">
          <div className="w-full min-w-0 whitespace-normal break-words break-words px-1 text-center text-xs leading-[14px] text-[rgba(255,255,255,0.7)]">
            {t('Interactions')}
          </div>
          <div className="text-base leading-5 text-white">
            {formatNumber(interactions ?? 0)}
          </div>
        </div>
        <div className="flex min-w-0 flex-1 flex-col items-center justify-between border-r border-r-secondary-border last:border-r-0">
          <div className="w-full min-w-0 whitespace-normal break-words break-words px-1 text-center text-xs leading-[14px] text-[rgba(255,255,255,0.7)]">
            {t('Memories')}
          </div>
          <div className="text-base leading-5 text-white">
            {formatNumber(memories ?? 0)}
          </div>
        </div>
        <div className="flex min-w-0 flex-1 flex-col items-center justify-between border-r border-r-secondary-border last:border-r-0">
          <div className="w-full min-w-0 whitespace-normal break-words break-words px-1 text-center text-xs leading-[14px] text-[rgba(255,255,255,0.7)]">
            {t('Connectors')}
          </div>
          <div className="text-base leading-5 text-white">
            {formatNumber(connectors ?? 0)}
          </div>
        </div>
      </div>
      {!hideDescriptionPreview && (
        <div className="break-all text-xs text-white">
          {replaceCharIntroductionPlaceHolders(
            introduction || '',
            item_name,
            characterGetResData?.character?.is_translated
              ? lang
              : DEFAULT_LOCALE
          )}
        </div>
      )}
    </div>
  )
}

export default RecentTalkCharacterProfileCard
